<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <v-card class="card-container d-flex pa-6" elevation="0">
        <v-row>
          <v-col cols="12">
            <span class="title-text">โปรไฟล์ของฉัน</span>
          </v-col>

          <v-col cols="12" v-if="admin">
            <ProfileInfoCard
              :data="admin"
              @save="handleSaveProfile"
              @avatar-change="handleAvatarChange"
            />
          </v-col>
          <v-col cols="12" v-if="admin" class="mt-4">
            <ProfilePasswordCard
              ref="passwordCard"
              :loading="changingPassword"
              @change-password="handleChangePassword"
            />
          </v-col>

          <v-col
            cols="12"
            v-else
            class="d-flex flex-column align-center justify-center"
            style="min-height: 70vh"
          >
            <div class="d-flex flex-column align-center">
              <v-img>
                <img
                  src="@/assets/not_exist_search.svg"
                  alt="no-data"
                  width="103"
                  height="103"
                />
              </v-img>

              <span class="no-data-text mb-4">ไม่พบข้อมูลผู้ใช้</span>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import ProfileInfoCard from "@/components/AdminProfile/ProfileInfoCard.vue";
import ProfilePasswordCard from "@/components/AdminProfile/ProfilePasswordCard.vue";
import { getAdminProfileApi } from "@/services/api/auth";
import {
  updateAdminProfileApi,
  updateAdminProfileImageApi,
  updateAdminPasswordApi,
} from "@/services/api/adminServices";
import swalAlert from "@/services/alert/swalServices";

const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}(?:\.[A-Za-z]{2,})*$/;

export default {
  name: "AdminProfile",
  components: { ProfileInfoCard, ProfilePasswordCard },

  data() {
    return {
      isLoading: false,
      admin: null,
      savingProfile: false,
      uploadingAvatar: false,
      changingPassword: false,
    };
  },

  created() {
    this.loadProfile();
  },

  methods: {
    buildErrorMessage(err, fallback) {
      return (
        err?.response?.data?.message ||
        (err?.response?.status
          ? `เกิดข้อผิดพลาด (HTTP ${err.response.status})`
          : fallback)
      );
    },

    async loadProfile() {
      this.isLoading = true;
      try {
        const res = await getAdminProfileApi();
        this.admin = res?.admin || null;
      } catch (err) {
        this.admin = null;
      } finally {
        this.isLoading = false;
      }
    },

    async handleSaveProfile(payload) {
      if (!this.admin) return;

      const phone = (payload.phone || "").trim();
      const email = (payload.email || "").trim();

      if (phone && !PHONE_REGEX.test(phone)) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากเบอร์โทรศัพท์ไม่ถูกต้อง"
        );
        return;
      }

      if (email && !EMAIL_REGEX.test(email)) {
        await swalAlert.Error(
          "ไม่สามารถบันทึกข้อมูลได้",
          "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากรูปแบบอีเมลไม่ถูกต้อง (เช่น name@example.com)"
        );
        return;
      }

      const res = await swalAlert.Dialog(
        "ยืนยันการแก้ไขข้อมูล",
        "คุณต้องการบันทึกการเปลี่ยนแปลงข้อมูลโปรไฟล์หรือไม่?",
        require("@/assets/alert/save.png"),
        132,
        121,
        "บันทึก",
        "ยกเลิก",
        true
      );

      if (!res.isConfirmed) return;

      this.savingProfile = true;

      try {
        const body = {
          firstName: payload.firstName,
          lastName: payload.lastName,
          phone,
          address: payload.address,
        };

        if (payload.username && payload.username !== this.admin.username) {
          body.username = payload.username;
        }

        if (email && email !== this.admin.email) {
          body.email = email;
        }

        const apiRes = await updateAdminProfileApi(body);
        if (apiRes?.admin) this.admin = apiRes.admin;

        await swalAlert.Success("แก้ไขโปรไฟล์สำเร็จ");
      } catch (err) {
        swalAlert.Error(
          "แก้ไขโปรไฟล์ไม่สำเร็จ",
          this.buildErrorMessage(err, "ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้")
        );
      } finally {
        this.savingProfile = false;
      }
    },

    async handleAvatarChange(file) {
      if (!file) return;
      this.uploadingAvatar = true;

      try {
        const res = await updateAdminProfileImageApi(file);
        if (res?.admin) this.admin = res.admin;

        await swalAlert.Success("แก้ไขรูปโปรไฟล์สำเร็จ");
      } catch (err) {
        swalAlert.Error(
          "อัปเดตรูปโปรไฟล์ไม่สำเร็จ",
          this.buildErrorMessage(
            err,
            "เกิดข้อผิดพลาดในการอัปโหลดรูป กรุณาลองใหม่อีกครั้ง"
          )
        );
      } finally {
        this.uploadingAvatar = false;
      }
    },

    async handleChangePassword(payload) {
      const { currentPassword, newPassword, confirmPassword } = payload || {};

      if (!currentPassword || !newPassword || !confirmPassword) {
        await swalAlert.Error(
          "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          "กรุณากรอกข้อมูลรหัสผ่านให้ครบถ้วน"
        );
        return;
      }

      if (String(newPassword).length < 8) {
        await swalAlert.Error(
          "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          "รหัสผ่านใหม่ต้องยาวอย่างน้อย 8 ตัวอักษร"
        );
        return;
      }

      if (newPassword !== confirmPassword) {
        await swalAlert.Error(
          "ไม่สามารถเปลี่ยนรหัสผ่านได้",
          "รหัสผ่านใหม่และยืนยันรหัสผ่านใหม่ไม่ตรงกัน"
        );
        return;
      }

      const res = await swalAlert.Dialog(
        "ยืนยันการเปลี่ยนรหัสผ่าน",
        "คุณต้องการเปลี่ยนรหัสผ่านใช่หรือไม่?",
        require("@/assets/alert/save.png"),
        132,
        121,
        "ยืนยัน",
        "ยกเลิก",
        true
      );

      if (!res.isConfirmed) return;

      this.changingPassword = true;

      try {
        const apiRes = await updateAdminPasswordApi({
          currentPassword,
          newPassword,
          confirmPassword,
        });

        const msg = apiRes?.message || "แก้ไขรหัสผ่านสำเร็จ";
        await swalAlert.Success(msg);

        if (this.$refs.passwordCard && this.$refs.passwordCard.reset) {
          this.$refs.passwordCard.reset();
        }
      } catch (err) {
        swalAlert.Error(
          "แก้ไขรหัสผ่านไม่สำเร็จ",
          this.buildErrorMessage(
            err,
            "ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาลองอีกครั้ง"
          )
        );
      } finally {
        this.changingPassword = false;
      }
    },
  },
};
</script>

<style scoped>
.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}
.no-data-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b717f;
}
.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}
</style>
